import { assertNever } from 'src/replicant/utils';
import { ViewPosition } from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import uiConfig from 'src/lib/ui/config';
import { LivingRoomPosition, Position } from './PositionModel';
import { width } from 'src/lib/ui/config/general';

export function getScenePosition(
  position: Position,
): Partial<ViewPosition & { zIndex: number }> {
  switch (position.scene) {
    case 'livingRoom':
    case 'friendsLivingRoom':
      return getLivingRoomPosition(position.location);

    case 'diningRoom':
      return getDiningRoomPosition(position.location);

    case 'toiletRoom':
      return getToiletRoomPosition(position.location);

    case 'bedroom':
      return getBedroomPosition(position.location);

    case 'bathroom':
      return getBathroomPosition(position.location);

    case 'minigame':
      return getMinigamePosition(position.location);

    case 'debut':
      return getDebutPosition(position.location);

    case '':
      return {};

    default:
      assertNever(position);
  }
}

function getDebutPosition(
  location: Extract<Position, { scene: 'debut' }>['location'],
) {
  const { height } = StateObserver.getState().ui.screenSize;
  switch (location) {
    case 'center': {
      return {
        x: width / 2,
        y: height * 0.758,
      };
    }

    case 'left': {
      return {
        x: 260,
        y: height * 0.757,
        scale: 0.85,
      };
    }

    case 'right': {
      return {
        x: 530,
        y: height * 0.758,
        scale: 0.85,
      };
    }

    case 'shardLeft': {
      return {
        x: 330,
        y: height * 0.758,
        scale: 0.85,
      };
    }

    case 'shardRight': {
      return {
        x: 460,
        y: height * 0.758,
        scale: 0.85,
      };
    }

    default:
      assertNever(location);
  }
}

function getLivingRoomPosition(location: LivingRoomPosition['location']) {
  const { height } = StateObserver.getState().ui.screenSize;
  const { width } = uiConfig;

  switch (location) {
    case 'tl':
      return {
        x: 127,
        y: height * 0.664,
      };

    case 'tc':
      return {
        x: width / 2,
        y: height * 0.664,
      };

    case 'tr':
      return {
        x: width * 0.764,
        y: height * 0.664,
      };

    case 'lm':
      return {
        x: 127,
        y: height * 0.734,
      };

    case 'c':
      return {
        x: width / 2,
        y: height * 0.734,
      };

    case 'mugCenter':
      return {
        x: width * 0.556,
        y: height * 0.723,
        scale: 0.65,
      };

    case 'rm':
      return {
        x: width * 0.764,
        y: height * 0.734,
      };

    case 'bl':
      return {
        x: width * 0.253,
        y: height * 0.773,
      };

    case 'bc':
      return {
        x: width / 2,
        y: height * 0.794,
      };

    case 'br':
      return {
        x: width * 0.764,
        y: height * 0.773,
      };

    case 'aboveLeft':
      return {
        x: 280,
        y: height * 0.44,
        zIndex: height,
      };

    case 'floorRight':
      return {
        x: width * 0.8,
        y: height * 0.734,
      };

    case 'floorFarRight':
      return {
        x: width * 0.9,
        y: height * 0.734,
      };

    case 'floorLeft':
      return {
        x: 250,
        y: height * 0.734,
      };

    case 'frontCenter':
      return {
        x: width / 2,
        y: height * 0.539,
        zIndex: height,
      };

    case 'frontLeft':
      return {
        x: 240,
        y: height * 0.539,
        zIndex: height,
      };

    default:
      assertNever(location);
  }
}

function getDiningRoomPosition(
  location: Extract<Position, { scene: 'diningRoom' }>['location'],
) {
  const { height } = StateObserver.getState().ui.screenSize;
  switch (location) {
    case 'mat': {
      return {
        x: 400,
        y: height * 0.73,
      };
    }

    case 'matPlayer': {
      return {
        x: 135,
        y: height * 0.687,
      };
    }

    case 'matFriend': {
      return {
        x: 590,
        y: height * 0.723,
      };
    }

    case 'plate': {
      return {
        x: 285,
        y: height * 0.71,
      };
    }

    case 'platePlayer': {
      return {
        x: 322,
        y: height * 0.665,
      };
    }

    case 'plateFriend': {
      return {
        x: 498,
        y: height * 0.69,
      };
    }

    default:
      assertNever(location);
  }
}

function getToiletRoomPosition(
  location: Extract<Position, { scene: 'toiletRoom' }>['location'],
) {
  const { height } = StateObserver.getState().ui.screenSize;
  switch (location) {
    case 'toilet': {
      return {
        x: 367,
        y: height * 0.616,
      };
    }

    case 'side': {
      return {
        x: 200,
        y: height * 0.781,
      };
    }

    default:
      assertNever(location);
  }
}

function getBedroomPosition(
  location: Extract<Position, { scene: 'bedroom' }>['location'],
) {
  const { height } = StateObserver.getState().ui.screenSize;
  const { width } = uiConfig;
  switch (location) {
    case 'bed': {
      return {
        x: width / 2,
        y: height * 0.638,
      };
    }

    default:
      assertNever(location);
  }
}

function getBathroomPosition(
  location: Extract<Position, { scene: 'bathroom' }>['location'],
) {
  const { height } = StateObserver.getState().ui.screenSize;
  switch (location) {
    case 'bath': {
      return {
        x: 346,
        y: height * 0.691,
      };
    }
    case 'side': {
      return {
        x: 326,
        y: height * 0.602,
      };
    }

    default:
      assertNever(location);
  }
}

function getMinigamePosition(
  location: Extract<Position, { scene: 'minigame' }>['location'],
) {
  const { height } = StateObserver.getState().ui.screenSize;
  const { width } = uiConfig;
  switch (location) {
    case 'platform': {
      return {
        x: width / 2,
        y: height * 0.411,
      };
    }

    default:
      assertNever(location);
  }
}
